/**
 * Entity Sync Service - Re-exports from generated service
 *
 * This file wraps the auto-generated entity sync service to provide:
 * - Stable import path that won't be overwritten by codegen
 * - All generated hooks and types for the unified entity sync API
 * - Helper constant for entity types
 */

import {
  BaseEntitySchema,
  CoverageEntitySchema,
  DataSuggestionsListResponseSchema,
  FilevineEntitySyncSuggestionSchema,
  FilevineEntitySyncSuggestionSetSchema,
  enhancedApi as generatedApi,
  MedicalProviderEntitySchema,
} from './generated/entitySyncService';

// Re-export all API arg and response types from generated service
export type {
  EntitySyncAcceptDataSuggestionApiArg,
  EntitySyncAcceptDataSuggestionApiResponse,
  EntitySyncApplyDataSuggestionApiArg,
  EntitySyncApplyDataSuggestionApiResponse,
  EntitySyncApproveLinkSuggestionApiArg,
  EntitySyncApproveLinkSuggestionApiResponse,
  // Commit
  EntitySyncCreateLinkApiArg,
  EntitySyncCreateLinkApiResponse,
  EntitySyncDismissDataSuggestionSetApiArg,
  EntitySyncDismissDataSuggestionSetApiResponse,
  // Field Config & Sync
  EntitySyncFieldConfigApiArg,
  EntitySyncFieldConfigApiResponse,
  EntitySyncGenerateDataSuggestionsApiArg,
  EntitySyncGenerateDataSuggestionsApiResponse,
  EntitySyncGenerateLinkSuggestionsApiArg,
  EntitySyncGenerateLinkSuggestionsApiResponse,
  EntitySyncGetDataSuggestionSetApiArg,
  EntitySyncGetDataSuggestionSetApiResponse,
  // Full Pipeline
  EntitySyncGetPipelineStatusApiArg,
  EntitySyncGetPipelineStatusApiResponse,
  // History
  // Ignore
  EntitySyncIgnoreFinchEntityApiArg,
  EntitySyncIgnoreFinchEntityApiResponse,
  // Data Suggestions
  EntitySyncListDataSuggestionsApiArg,
  EntitySyncListDataSuggestionsApiResponse,
  // Links
  EntitySyncListLinksApiArg,
  EntitySyncListLinksApiResponse,
  // Link Suggestions
  EntitySyncListLinkSuggestionsApiArg,
  EntitySyncListLinkSuggestionsApiResponse,
  // Pairs
  EntitySyncListPairsApiArg,
  EntitySyncListPairsApiResponse,
  EntitySyncRejectDataSuggestionApiArg,
  EntitySyncRejectDataSuggestionApiResponse,
  EntitySyncRejectLinkSuggestionApiArg,
  EntitySyncRejectLinkSuggestionApiResponse,
  EntitySyncRemoveLinkApiArg,
  EntitySyncRemoveLinkApiResponse,
  EntitySyncRunFullPipelineApiArg,
  EntitySyncRunFullPipelineApiResponse,
  EntitySyncTriggerSyncApiArg,
  EntitySyncTriggerSyncApiResponse,
} from './generated/entitySyncService';

// Re-export domain types from generated service
// NOTE: Types that are also in filevineCollectionService are intentionally NOT
// re-exported here to avoid duplicate export errors. Import them from
// filevineCollectionService instead.
export type {
  // Request/Response schemas (new to entity sync)
  AcceptDataSuggestionRequestSchema,
  // Enum types (new to entity sync)
  CanonicalSectionType,
  CreateLinkRequestSchema,
  DataSuggestionsListResponseSchema,
  // Core schema types (new to entity sync)
  EntityPairSchema,
  EntityPairSchemaRead,
  EntityPairsResponseSchema,
  EntityPairsResponseSchemaRead,
  // NOTE: GenerateLinkSuggestionsResponseSchema is exported from filevineCollectionService
  // to avoid duplicate exports during migration
  GenerateSuggestionsRequestSchema,
  GenerateSuggestionsResponseSchema,
  IgnoreFinchEntityRequestSchema,
  SyncStatus,
} from './generated/entitySyncService';

// Export the API for store configuration
export type EntitySyncReviewStatus =
  | 'PENDING_REVIEW'
  | 'ACCEPTED'
  | 'MODIFIED';

export type FilevineEntitySyncSuggestionSchemaWithReview =
  FilevineEntitySyncSuggestionSchema & {
    is_auto_pushed?: boolean;
    review_status?: EntitySyncReviewStatus | null;
    source_suggestion_id?: string | null;
  };

export type FilevineEntitySyncSuggestionSetSchemaWithReview =
  Omit<FilevineEntitySyncSuggestionSetSchema, 'suggestions'> & {
    suggestions?: FilevineEntitySyncSuggestionSchemaWithReview[];
  };

export type DataSuggestionsListResponseSchemaWithReview =
  Omit<DataSuggestionsListResponseSchema, 'suggestion_sets'> & {
    suggestion_sets?: FilevineEntitySyncSuggestionSetSchemaWithReview[];
  };

export type EntitySyncListDataSuggestionsV2ApiArg = {
  entityType: string;
  matterId: string;
  status?: string;
  autoPushedOnly?: boolean;
  reviewStatus?: EntitySyncReviewStatus;
};

const entitySyncApiWithReview = generatedApi.injectEndpoints({
  endpoints: (build) => ({
    entitySyncListDataSuggestionsV2: build.query<
      DataSuggestionsListResponseSchemaWithReview,
      EntitySyncListDataSuggestionsV2ApiArg
    >({
      query: (queryArg) => ({
        url: `/api/matters/${queryArg.matterId}/entity-sync/${queryArg.entityType}/data-suggestions/`,
        params: {
          status: queryArg.status,
          auto_pushed_only: queryArg.autoPushedOnly,
          review_status: queryArg.reviewStatus,
        },
      }),
      providesTags: ['matters'],
    }),
  }),
  overrideExisting: false,
});

export const entitySyncApi = entitySyncApiWithReview;

// Export hooks from the API
export const {
  // Data Suggestions hooks
  useEntitySyncListDataSuggestionsQuery,
  useEntitySyncGetDataSuggestionSetQuery,
  useEntitySyncDismissDataSuggestionSetMutation,
  useEntitySyncAcceptDataSuggestionMutation,
  useEntitySyncApplyDataSuggestionMutation,
  useEntitySyncRejectDataSuggestionMutation,
  useEntitySyncGenerateDataSuggestionsMutation,
  // Field Config & Sync hooks
  useEntitySyncFieldConfigQuery,
  useEntitySyncTriggerSyncMutation,
  // Full Pipeline hooks
  useEntitySyncRunFullPipelineMutation,
  useEntitySyncGetPipelineStatusQuery,
  // History hooks
  // Commit hooks
  // Link Suggestions hooks
  useEntitySyncListLinkSuggestionsQuery,
  useEntitySyncGenerateLinkSuggestionsMutation,
  useEntitySyncApproveLinkSuggestionMutation,
  useEntitySyncRejectLinkSuggestionMutation,
  // Links hooks
  useEntitySyncListLinksQuery,
  useEntitySyncCreateLinkMutation,
  useEntitySyncRemoveLinkMutation,
  useEntitySyncIgnoreFinchEntityMutation,
  // Pairs hooks
  useEntitySyncListPairsQuery,
} = generatedApi;

export const { useEntitySyncListDataSuggestionsV2Query } = entitySyncApiWithReview;

/**
 * Entity type constants for type-safe entity type parameters.
 * Use these instead of string literals when calling entity sync endpoints.
 */
export const ENTITY_SYNC_TYPES = {
  MEDICAL_PROVIDERS: 'medical_providers',
  COVERAGE: 'coverage',
} as const;

export type EntitySyncType =
  (typeof ENTITY_SYNC_TYPES)[keyof typeof ENTITY_SYNC_TYPES];

/**
 * Filevine custom field types enum.
 * Used to determine how to render field values in the UI.
 */
export enum CustomFieldType {
  INTEGER = 1,
  CURRENCY = 2,
  TEXT = 3,
  DATE = 4,
  BOOLEAN = 5,
  MULTILINE_TEXT = 6,
  PERSON = 7,
  ATTACHMENT = 8,
  DROPDOWN = 9,
  DOCUMENT = 11,
  PERCENTAGE = 14,
  DEADLINE = 15,
  DATETIME = 18,
  STRING_LIST = 24,
  MULTI_SELECT = 26,
  PROJECT_LINK = 28,
  DOC_GEN_BUTTON = 29,
  DOC_GEN_AI_BUTTON = 1100,
}

export type FinchEntity =
  | MedicalProviderEntitySchema
  | CoverageEntitySchema
  | BaseEntitySchema;

export type ContactHandleSchema = NonNullable<
  MedicalProviderEntitySchema['handles']
>[number];
